import {
  useMutation,
  FetchResult,
  useLazyQuery,
  QueryLazyOptions,
  OperationVariables,
} from "@apollo/client";

import {
  CELLCARD_VERIFY_DOCUMENT,
  SIGN_IN_OAUTH2,
  REQUEST_EVENTS,
  VERIFY_EVENTS,
} from "./login.mutations";
import { GET_LEGAL_TERMS } from "./login.queries";

interface VerifyCellcardDocumentProps {
  variables: {
    document: string;
  };
}

export interface UseGetLegalTermProps {
  data: any;
  error: any;
  loading: boolean;
  getTerms: (
    options?: QueryLazyOptions<OperationVariables> | undefined
  ) => void;
}

interface SignInProps {
  variables: {
    document?: string;
    store?: string;
    login: string;
    password: string;
  };
}

export function useSignInAuth(): {
  verifyCellcardDocument: (
    options: VerifyCellcardDocumentProps
  ) => Promise<FetchResult>;
  signIn: (options: SignInProps) => Promise<FetchResult>;
} {
  const [mutateVerifyCellcardDocument] = useMutation(CELLCARD_VERIFY_DOCUMENT);
  const [mutateSignIn] = useMutation(SIGN_IN_OAUTH2);

  function verifyCellcardDocument(
    options: VerifyCellcardDocumentProps
  ): Promise<FetchResult> {
    return mutateVerifyCellcardDocument(options);
  }

  function signIn(options: SignInProps): Promise<FetchResult> {
    return mutateSignIn(options);
  }

  return { verifyCellcardDocument, signIn };
}

export const useGetLegalTerm = (): UseGetLegalTermProps => {
  const [getTerms, { loading, data, error }] = useLazyQuery(GET_LEGAL_TERMS);

  return { loading, data, error, getTerms };
};

interface RequestEventsProps {
  variables: {
    kind: "reset_password";
    store?: string;
    login: string;
    document?: string;
  };
}

interface VerifyEventsProps {
  variables: {
    token: string;
    id: string;
    document?: string;
  };
}

export const useRequestEvents = (): {
  requestEvents: (options: RequestEventsProps) => Promise<FetchResult>;
  requestEventsLoading: boolean;
} => {
  const [requestEventsMutation, { loading }] = useMutation(REQUEST_EVENTS);

  const requestEvents = (options: RequestEventsProps): Promise<FetchResult> => {
    return requestEventsMutation(options);
  };

  return { requestEvents, requestEventsLoading: loading };
};

export const useVerifyEvents = (): {
  verifyEvents: (options: VerifyEventsProps) => Promise<FetchResult>;
} => {
  const [verifyEventsMutation] = useMutation(VERIFY_EVENTS);

  const verifyEvents = (options: VerifyEventsProps): Promise<FetchResult> => {
    return verifyEventsMutation(options);
  };

  return { verifyEvents };
};
